import type { UseQueryResult } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export enum ParticipationType {
  User = 'user',
  Group = 'group'
}

export enum TestType {
  Standard = 'standard',
  Adaptive = 'adaptive',
  Survey = 'survey'
}

export type TokenCreationLimit = 'multiple' | 'single';

export type BaseTest = API_JSON & {
  identifier: string;
  name: string;
  description: string;
  notes: string;
  type: TestType;
  onlineOnly: boolean;
  externalId: string;
  numberOfItems: number;
  levels: API_JSON[];
  tokenCreationLimit: TokenCreationLimit;
  participationTypes: (API_JSON & { name: string })[];
};

const order = {
  'order[order]': 'asc',
  'order[id]': 'asc'
} as const;

const groups = [
  'test-identifier',
  'test-name',
  'test-description',
  'test-levels',
  'test-notes',
  'test-type',
  'test-participationtypes',
  'test-onlineOnly',
  'test-externalid'
];

const include = 'participationTypes';
/**
 * Fetch tests
 * @param params can be used to customise/limit the tests retrieved
 * @returns an array of tests
 */
export const getTests: GetMany<BaseTest> = async (params) =>
  (await ASSIST_API.get('tests', { params: requestParams({ order, groups, include }, params) }))
    .data;

/**
 * Fetch test (singular)
 * @param params can be used to customise the test retrieved
 * @returns a single test
 */
export const getTest: GetOne<BaseTest> = async (id, params) =>
  (
    await ASSIST_API.get(`tests/${id}`, {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Hook implementation to fetch tests
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns tests matching request
 */
export const useTests: AssistQuery.UseMany<BaseTest> = (params, options) =>
  useQueryAssist(['tests', params], () => getTests(params), { staleTime: STALE_LONG, ...options });

/**
 * Given an testId fetch and return the product
 *  - only runs once testId is provided
 *  - fetches the one test
 *  - caches data
 * @param id testId
 * @param params optional parameters to change requested data
 * @returns the matching test
 */
export const useTest: AssistQuery.UseOne<BaseTest> = (id, params, options) =>
  useQueryAssist(['test', { id, ...params }], () => getTest(id, params), {
    enabled: !!id,
    staleTime: STALE_LONG,
    ...options
  });

export const useTestsByArea = (
  areaId: number,
  params?: RequestParams
): UseQueryResult<RequestResponse<BaseTest[]>> =>
  useQueryAssist(
    ['test', { ...params, filters: { area: areaId } }],
    () => getTests({ ...params, filters: { area: areaId } }),
    { staleTime: STALE_LONG }
  );
